import { useEffect, useMemo, useRef, useState } from "react";
import AMLTable from "./table";
import Pagination from "../../Components/Pagination/Pagination";
import SearchInput from "../../Components/Inputs/SearchInput";
import EmailFilterDropdown from "../../Components/Dropdown/EmailFilterDropdown";
import dayjs from "dayjs";
import {
  collection,
  collectionGroup,
  getDocs,
  query,
  orderBy,
  limit,
  where,
  startAt,
  endAt,
  startAfter,
  getCountFromServer,
  doc,
  getDoc,
  onSnapshot,
  documentId,
} from "firebase/firestore";
import { dbAML as db } from "../../../../../backend/firebaseconfig";
import useDebounce from "../utils/useDebounce";
import { useNavigate } from "react-router-dom";
import NewCheckButton from "../../Components/Buttons/NewCheckButton";
import { Spinner } from "../../Components/Spinner/spinner";
import { useUserEmail } from "../../../../../context/UserEmailContext";
import { useUserRole } from "../../../../../context/UserRoleContext";
import { useUserPermissions } from "../../../../../context/UserPermissionsContext";
import { useAdminAdviserMails } from "../../../../../context/AdminAdviserEmailsContext";
import StatusButton from "../../Components/Buttons/StatusButton";
import Announcement from "../utils/getAnnouncements";
import { getRelatedEmails } from "../../Utils/getRelatedEmails";
import { ReactComponent as Check2Logo } from "../../../../../assets/icons/aml/check2_logo.svg";
import "../../../../../css/Components/DesignSystem/RadioButton.css";

// —— helpers ——
const normalise = (s) => (s || "").toString().trim().toLowerCase();

const classifyBucket = (d) => {
  const isTest =
    d.isTest === true ||
    normalise(d.environment) === "test" ||
    normalise(d.mode) === "test" ||
    normalise(d.checkType) === "test";
  if (isTest) return "test";

  const ct = normalise(d.checkType);

  if (ct === "enhanced" || ct === "idv" || ct === "identity verification")
    return "idv";

  if (ct === "standard" || ct === "document only" || ct === "document")
    return "doconly";

  if (ct === "simple") return "dbWs";

  const docOnlyHint =
    d.docOnly === true ||
    normalise(d.flow) === "document_only" ||
    normalise(d.veriffFlow) === "document_only";
  if (docOnlyHint) return "doconly";

  return "idv";
};

const idsParam = (arr) => arr.map(encodeURIComponent).join(",");

const looksLikeVendorIdSearch = (terms, rawQuery) => {
  if (terms.length !== 1) return false;
  const token = String(rawQuery || "").trim();
  if (!token) return false;

  // Avoid forcing index lookups for very short free-text searches.
  return token.length >= 6;
};

const pick = (obj, ...paths) =>
  paths.reduce(
    (acc, p) =>
      (acc ??= p.split(".").reduce((o, k) => (o ? o[k] : undefined), obj)),
    undefined,
  );

function makeDocSearchHaystack(checkId, data) {
  const pieces = [
    data?.sessionId || "",
    data?.vendorData || "",
    data?.checkType || "",
    data?.createdBy || "",
    data?.forAdviser || "",
    checkId || "",
  ];

  return normalise(pieces.join(" "));
}

function makeHaystack(row) {
  const first = row?.decision?.verification?.person?.firstName || "";
  const last = row?.decision?.verification?.person?.lastName || "";
  const createdOn = row?.decision?.verification?.acceptanceTime
    ? dayjs(row.decision.verification.acceptanceTime).format("DD/MM/YYYY")
    : "";

  const pieces = [
    `${first} ${last}`,
    row?.createdAt
      ? dayjs(
          row.createdAt.toDate ? row.createdAt.toDate() : row.createdAt,
        ).format("DD/MM/YYYY")
      : "",
    row?.checkType || "",
    row?.createdBy || "",
    row?.ownerEmail || "",
    row?.vendorData || "",
    row?.id || "",
    createdOn,
  ];

  return normalise(pieces.join(" "));
}

const canViewAllChecks = (role, perms = []) =>
  role === "Staff" && Array.isArray(perms) && perms.includes("Check ² Admin");

const DASHBOARD_STORAGE_KEY = "aml_dashboard_state_v1";

export default function AmlRows() {
  const { userEmail } = useUserEmail();
  const { userRole } = useUserRole();
  const { userPermissions } = useUserPermissions();
  const { adminAdviserMails } = useAdminAdviserMails();
  const navigate = useNavigate();

  const viewAll = canViewAllChecks(userRole, userPermissions);

  // restore persisted dashboard state
  const getSavedState = () => {
    try {
      const raw = sessionStorage.getItem(DASHBOARD_STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  const savedStateRef = useRef(getSavedState());

  const [relatedEmails, setRelatedEmails] = useState([]);
  const [emailOptions, setEmailOptions] = useState([]);

  const [selectedEmail, setSelectedEmail] = useState(
    savedStateRef.current?.selectedEmail || "",
  );
  const [submittedViaApiOnly, setSubmittedViaApiOnly] = useState(
    savedStateRef.current?.submittedViaApiOnly === true,
  );

  const [q, _setQ] = useState(savedStateRef.current?.q || "");
  const setQ = (v) =>
    _setQ(typeof v === "string" ? v : (v?.target?.value ?? ""));
  const debouncedQ = useDebounce(q, 250);

  const terms = useMemo(
    () => normalise(debouncedQ).split(/\s+/).filter(Boolean),
    [debouncedQ],
  );

  const pageSize = 15;
  const [page, setPage] = useState(savedStateRef.current?.page || 1);
  const [totalPages, setTotalPages] = useState(1);

  const cursorsRef = useRef([]);
  const [pageDocs, setPageDocs] = useState([]);
  const SEARCH_FETCH_CAP = 6000;
  const [searchDocs, setSearchDocs] = useState([]);
  const [rows, setRows] = useState([]);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const lastDecisionKeyRef = useRef("");
  const lastMergedRef = useRef([]);
  const lastFetchedAtRef = useRef(0);
  const lastModeRef = useRef("page");

  const [err, setErr] = useState("");
  const [fetchingDocs, setFetchingDocs] = useState(false);
  const [hydratingRows, setHydratingRows] = useState(false);
  const loading = fetchingDocs || hydratingRows;
  const isSearchMode = terms.length > 0;
  const isSearching = isSearchMode && loading;

  const API_BASE = process.env.REACT_APP_AML_SERVER;

  const baseOrder = useMemo(
    () => [orderBy("createdAt", "desc"), orderBy(documentId(), "desc")],
    [],
  );

  // persist dashboard state
  useEffect(() => {
    try {
      sessionStorage.setItem(
        DASHBOARD_STORAGE_KEY,
        JSON.stringify({
          page,
          q,
          selectedEmail,
          submittedViaApiOnly,
        }),
      );
    } catch {
      // ignore storage errors
    }
  }, [page, q, selectedEmail, submittedViaApiOnly]);

  // fetch related emails for non-staff view
  useEffect(() => {
    if (!userEmail || !userRole || viewAll) {
      setRelatedEmails([]);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const emails = await getRelatedEmails(userEmail, userRole);
        const withContext = new Set(emails);

        if (Array.isArray(adminAdviserMails)) {
          adminAdviserMails.forEach((email) => {
            const normalized = String(email || "").trim();
            if (normalized) withContext.add(normalized);
          });
        }

        if (!cancelled) {
          setRelatedEmails(Array.from(withContext));
        }
      } catch (error) {
        // console.error("Failed to fetch related emails:", error);
        if (!cancelled) {
          setRelatedEmails([userEmail]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userEmail, userRole, viewAll, adminAdviserMails]);

  // fetch dropdown email doc ids for staff/admin
  // build dropdown options for staff from actual checks
  useEffect(() => {
    if (!viewAll) {
      setEmailOptions([]);
      setSelectedEmail("");
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const snap = await getDocs(
          query(collectionGroup(db, "Checks"), ...baseOrder, limit(6000)),
        );

        const uniqueEmails = new Set();

        snap.docs.forEach((docSnap) => {
          const ownerEmail = docSnap.ref?.parent?.parent?.id;
          if (ownerEmail) uniqueEmails.add(ownerEmail);

          const data = docSnap.data();
          if (data?.createdBy) uniqueEmails.add(String(data.createdBy).trim());
          if (data?.forAdviser)
            uniqueEmails.add(String(data.forAdviser).trim());
        });

        const emails = Array.from(uniqueEmails)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));

        if (!cancelled) {
          setEmailOptions(
            emails.map((email) => ({
              label: email,
              value: email,
            })),
          );
        }
      } catch (e) {
        // console.error("Failed to fetch staff email options:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [viewAll, baseOrder]);

  const makePageQuery = (targetPage) => {
    const staffRef = selectedEmail
      ? collection(db, `AML Checks/${selectedEmail}/Checks`)
      : collectionGroup(db, "Checks");

    let qy = viewAll
      ? query(staffRef, ...baseOrder, limit(pageSize))
      : query(
          collection(db, `AML Checks/${userEmail}/Checks`),
          ...baseOrder,
          limit(pageSize),
        );

    if (targetPage > 1) {
      const lastFromPrev = cursorsRef.current[targetPage - 1];
      if (lastFromPrev) {
        qy = viewAll
          ? query(
              staffRef,
              ...baseOrder,
              startAfter(lastFromPrev),
              limit(pageSize),
            )
          : query(
              collection(db, `AML Checks/${userEmail}/Checks`),
              ...baseOrder,
              startAfter(lastFromPrev),
              limit(pageSize),
            );
      }
    }

    return qy;
  };

  const runIndexedVendorSearch = async (vendorTerm) => {
    const term = String(vendorTerm || "").trim();
    if (!term) return [];

    const toResult = (docSnap) => ({
      id: docSnap.id,
      data: docSnap.data(),
      _snap: docSnap,
    });

    const dedupeKey = (row) => {
      const ownerEmail = row?._snap?.ref?.parent?.parent?.id || "";
      return `${ownerEmail}::${row.id}`;
    };

    if (viewAll) {
      const staffRef = selectedEmail
        ? collection(db, `AML Checks/${selectedEmail}/Checks`)
        : collectionGroup(db, "Checks");

      // Reliable exact lookup across all owners uses persisted vendorData.
      const exactByFieldSnap = await getDocs(
        query(
          staffRef,
          where("vendorData", "==", term),
          limit(SEARCH_FETCH_CAP),
        ),
      );

      if (!exactByFieldSnap.empty) {
        return exactByFieldSnap.docs.map(toResult);
      }

      // Prefix ID lookup is safest when the parent adviser scope is selected.
      if (selectedEmail) {
        const exactByIdSnap = await getDocs(
          query(staffRef, where(documentId(), "==", term), limit(1)),
        );

        if (!exactByIdSnap.empty) {
          return exactByIdSnap.docs.map(toResult);
        }

        const prefixSnap = await getDocs(
          query(
            staffRef,
            orderBy(documentId()),
            startAt(term),
            endAt(`${term}\uf8ff`),
            limit(SEARCH_FETCH_CAP),
          ),
        );

        return prefixSnap.docs.map(toResult);
      }

      return [];
    }

    const emailsToQuery =
      relatedEmails.length > 0 ? relatedEmails : [userEmail];

    const exactByFieldSnaps = await Promise.all(
      emailsToQuery.map((email) =>
        getDocs(
          query(
            collection(db, `AML Checks/${email}/Checks`),
            where("vendorData", "==", term),
            limit(SEARCH_FETCH_CAP),
          ),
        ),
      ),
    );

    const exactFieldRows = exactByFieldSnaps.flatMap((snap) =>
      snap.docs.map(toResult),
    );
    if (exactFieldRows.length > 0) {
      const seen = new Set();
      return exactFieldRows.filter((row) => {
        const key = dedupeKey(row);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }

    const exactSnaps = await Promise.all(
      emailsToQuery.map((email) =>
        getDocs(
          query(
            collection(db, `AML Checks/${email}/Checks`),
            where(documentId(), "==", term),
            limit(1),
          ),
        ),
      ),
    );

    const exactRows = exactSnaps.flatMap((snap) => snap.docs.map(toResult));
    if (exactRows.length > 0) {
      const seen = new Set();
      return exactRows.filter((row) => {
        const key = dedupeKey(row);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }

    const prefixSnaps = await Promise.all(
      emailsToQuery.map((email) =>
        getDocs(
          query(
            collection(db, `AML Checks/${email}/Checks`),
            orderBy(documentId()),
            startAt(term),
            endAt(`${term}\uf8ff`),
            limit(SEARCH_FETCH_CAP),
          ),
        ),
      ),
    );

    const allRows = prefixSnaps.flatMap((snap) => snap.docs.map(toResult));
    const seen = new Set();

    return allRows.filter((row) => {
      const key = dedupeKey(row);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  // mode reset
  useEffect(() => {
    const mode = isSearchMode ? "search" : "page";
    if (mode !== lastModeRef.current) {
      lastModeRef.current = mode;
      lastDecisionKeyRef.current = "";
      lastMergedRef.current = [];
      lastFetchedAtRef.current = 0;
    }
  }, [isSearchMode]);

  useEffect(() => {
    lastDecisionKeyRef.current = "";
    lastMergedRef.current = [];
  }, [page, selectedEmail, submittedViaApiOnly]);

  // when scope changes, clear cursor cache
  useEffect(() => {
    cursorsRef.current = [];
  }, [userEmail, selectedEmail, submittedViaApiOnly]);

  // only reset to first page when filter/search changes by user interaction,
  // but do not force-reset on first load from saved session state
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    setPage(1);
    cursorsRef.current = [];
  }, [debouncedQ, selectedEmail, submittedViaApiOnly]);

  // ===== DEFAULT COUNT =====
  useEffect(() => {
    if (!userEmail || isSearchMode) return;

    let cancelled = false;

    (async () => {
      try {
        let total = 0;

        if (viewAll) {
          const staffRef = selectedEmail
            ? collection(db, `AML Checks/${selectedEmail}/Checks`)
            : collectionGroup(db, "Checks");
          const baseQ = submittedViaApiOnly
            ? query(staffRef, where("submittedViaApi", "==", true))
            : query(staffRef);

          const countSnap = await getCountFromServer(baseQ);
          total = countSnap.data().count || 0;
        } else {
          const emailsToQuery =
            relatedEmails.length > 0 ? relatedEmails : [userEmail];

          const countPromises = emailsToQuery.map(async (email) => {
            const baseQ = query(collection(db, `AML Checks/${email}/Checks`));
            const countSnap = await getCountFromServer(baseQ);
            return countSnap.data().count || 0;
          });

          const counts = await Promise.all(countPromises);
          total = counts.reduce((sum, count) => sum + count, 0);
        }

        const pages = Math.max(1, Math.ceil(total / pageSize));
        if (!cancelled) setTotalPages(pages);
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Failed to count checks");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    userEmail,
    isSearchMode,
    viewAll,
    pageSize,
    relatedEmails,
    selectedEmail,
    submittedViaApiOnly,
  ]);

  // ===== DEFAULT PAGINATION =====
  useEffect(() => {
    if (isSearchMode) return;
    if (!userEmail) return;

    const unsubscribers = [];
    let cancelled = false;

    (async () => {
      try {
        setErr("");
        setFetchingDocs(true);

        if (viewAll) {
          if (submittedViaApiOnly) {
            const staffRef = selectedEmail
              ? collection(db, `AML Checks/${selectedEmail}/Checks`)
              : collectionGroup(db, "Checks");
            const apiOnlyQuery = query(
              staffRef,
              where("submittedViaApi", "==", true),
              limit(SEARCH_FETCH_CAP),
            );

            const unsub = onSnapshot(
              apiOnlyQuery,
              (snap) => {
                const docs = snap.docs
                  .map((d) => ({ id: d.id, data: d.data(), _snap: d }))
                  .sort((a, b) => {
                    const aDate =
                      a.data?.createdAt?.toDate?.() ||
                      new Date(a.data?.createdAt || 0);
                    const bDate =
                      b.data?.createdAt?.toDate?.() ||
                      new Date(b.data?.createdAt || 0);
                    return bDate - aDate;
                  });
                const start = (page - 1) * pageSize;
                setPageDocs(docs.slice(start, start + pageSize));
                setFetchingDocs(false);
              },
              (e) => {
                setErr(e?.message || "Failed to load API submissions");
                setFetchingDocs(false);
              },
            );

            unsubscribers.push(unsub);
            return;
          }

          const wantPage = Math.max(1, page);

          if (wantPage > 1 && !cursorsRef.current[wantPage - 1]) {
            for (let i = 1; i < wantPage; i++) {
              if (cancelled) return;
              const snap = await getDocs(makePageQuery(i));
              cursorsRef.current[i] = snap.docs[snap.docs.length - 1] || null;
            }
          }

          const unsub = onSnapshot(
            makePageQuery(wantPage),
            (snap) => {
              const docs = snap.docs.map((d) => ({
                id: d.id,
                data: d.data(),
                _snap: d,
              }));

              setPageDocs(docs);
              cursorsRef.current[wantPage] =
                snap.docs[snap.docs.length - 1] || null;
              setFetchingDocs(false);
            },
            (e) => {
              setErr(e?.message || "Failed to load page");
              setFetchingDocs(false);
            },
          );

          unsubscribers.push(unsub);
        } else {
          const emailsToQuery =
            relatedEmails.length > 0 ? relatedEmails : [userEmail];
          const allDocs = [];

          emailsToQuery.forEach((email) => {
            const emailQuery = query(
              collection(db, `AML Checks/${email}/Checks`),
              ...baseOrder,
              limit(pageSize * 10),
            );

            const unsub = onSnapshot(
              emailQuery,
              (snap) => {
                const filteredDocs = allDocs.filter(
                  (d) => d._snap?.ref?.parent?.parent?.id !== email,
                );

                const newDocs = snap.docs.map((d) => ({
                  id: d.id,
                  data: d.data(),
                  _snap: d,
                }));

                const merged = [...filteredDocs, ...newDocs];

                merged.sort((a, b) => {
                  const aDate =
                    a.data?.createdAt?.toDate?.() ||
                    new Date(a.data?.createdAt || 0);
                  const bDate =
                    b.data?.createdAt?.toDate?.() ||
                    new Date(b.data?.createdAt || 0);
                  return bDate - aDate;
                });

                allDocs.length = 0;
                allDocs.push(...merged);

                const startIdx = (page - 1) * pageSize;
                const endIdx = startIdx + pageSize;
                setPageDocs(merged.slice(startIdx, endIdx));
                setFetchingDocs(false);
              },
              (e) => {
                setErr(e?.message || `Failed to load checks for ${email}`);
                setFetchingDocs(false);
              },
            );

            unsubscribers.push(unsub);
          });
        }
      } catch (e) {
        setErr(e?.message || "Failed to load page");
        setFetchingDocs(false);
      }
    })();

    return () => {
      cancelled = true;
      unsubscribers.forEach((unsub) => unsub && unsub());
    };
  }, [
    page,
    userEmail,
    isSearchMode,
    viewAll,
    relatedEmails,
    selectedEmail,
    submittedViaApiOnly,
    baseOrder,
  ]);

  // ===== SEARCH MODE =====
  useEffect(() => {
    if (!userEmail) return;

    if (!isSearchMode) {
      setSearchDocs([]);
      return;
    }

    setErr("");
    setFetchingDocs(true);

    let cancelled = false;
    const unsubscribers = [];

    const maybeIndexedVendorSearch = async () => {
      try {
        const useIndexSearch = looksLikeVendorIdSearch(terms, debouncedQ);
        if (!useIndexSearch) return false;

        const indexedDocs = await runIndexedVendorSearch(debouncedQ);

        // If indexed lookup finds results, prefer it immediately.
        if (indexedDocs.length > 0) {
          setSearchDocs(indexedDocs);
          setFetchingDocs(false);
          return true;
        }

        return false;
      } catch {
        // Fall back to broad search if indexed path fails.
        return false;
      }
    };

    (async () => {
      const handled = await maybeIndexedVendorSearch();
      if (cancelled || handled) return;

      if (viewAll) {
        const baseQ = selectedEmail
          ? query(
              collection(db, `AML Checks/${selectedEmail}/Checks`),
              ...baseOrder,
              limit(SEARCH_FETCH_CAP),
            )
          : query(
              collectionGroup(db, "Checks"),
              ...baseOrder,
              limit(SEARCH_FETCH_CAP),
            );

        const unsub = onSnapshot(
          baseQ,
          (snap) => {
            const docs = snap.docs.map((d) => ({
              id: d.id,
              data: d.data(),
              _snap: d,
            }));
            setSearchDocs(docs);
            setFetchingDocs(false);
          },
          (e) => {
            setErr(e?.message || "Failed to run search");
            setFetchingDocs(false);
          },
        );

        unsubscribers.push(unsub);
        return;
      }

      const emailsToQuery =
        relatedEmails.length > 0 ? relatedEmails : [userEmail];
      const allDocs = [];

      emailsToQuery.forEach((email) => {
        const baseQ = query(
          collection(db, `AML Checks/${email}/Checks`),
          ...baseOrder,
          limit(SEARCH_FETCH_CAP),
        );

        const unsub = onSnapshot(
          baseQ,
          (snap) => {
            const filteredDocs = allDocs.filter(
              (d) => d._snap?.ref?.parent?.parent?.id !== email,
            );

            const newDocs = snap.docs.map((d) => ({
              id: d.id,
              data: d.data(),
              _snap: d,
            }));

            const merged = [...filteredDocs, ...newDocs];

            merged.sort((a, b) => {
              const aDate =
                a.data?.createdAt?.toDate?.() ||
                new Date(a.data?.createdAt || 0);
              const bDate =
                b.data?.createdAt?.toDate?.() ||
                new Date(b.data?.createdAt || 0);
              return bDate - aDate;
            });

            allDocs.length = 0;
            allDocs.push(...merged);

            setSearchDocs(merged.slice(0, SEARCH_FETCH_CAP));
            setFetchingDocs(false);
          },
          (e) => {
            setErr(e?.message || `Failed to run search for ${email}`);
            setFetchingDocs(false);
          },
        );

        unsubscribers.push(unsub);
      });
    })();

    return () => {
      cancelled = true;
      unsubscribers.forEach((unsub) => unsub && unsub());
    };
  }, [
    userEmail,
    isSearchMode,
    viewAll,
    baseOrder,
    relatedEmails,
    selectedEmail,
    terms,
    debouncedQ,
  ]);

  async function fetchRequestDataForDocs(docs) {
    const tasks = docs.map(async ({ id: checkId, data, _snap }) => {
      const ownerEmail = _snap?.ref?.parent?.parent?.id || userEmail;
      const basePath = `AML Checks/${ownerEmail}/Checks/${checkId}/Request Data`;
      const explicitId = data?.requestDataId;

      try {
        if (explicitId) {
          const reqRef = doc(db, basePath, explicitId);
          const reqSnap = await getDoc(reqRef);
          return {
            checkId,
            requestData: reqSnap.exists() ? reqSnap.data() : null,
            ownerEmail,
          };
        }

        const reqCol = collection(db, basePath);
        const reqSnap = await getDocs(reqCol);
        const first = reqSnap.docs[0];

        return {
          checkId,
          requestData: first ? first.data() : null,
          ownerEmail,
        };
      } catch {
        return { checkId, requestData: null, ownerEmail };
      }
    });

    const results = await Promise.all(tasks);
    return new Map(
      results.map((r) => [
        r.checkId,
        { requestData: r.requestData, ownerEmail: r.ownerEmail },
      ]),
    );
  }

  async function fetchExperianResponseForDocs(docs) {
    const tasks = docs.map(async ({ id: checkId, _snap }) => {
      const ownerEmail = _snap?.ref?.parent?.parent?.id || userEmail;
      const experianPath = `AML Checks/${ownerEmail}/Checks/${checkId}/Experian Response`;

      try {
        const snap = await getDocs(collection(db, experianPath));
        const first = snap.docs[0];
        const data = first ? first.data() : null;

        return {
          checkId,
          ownerEmail,
          experianResponse: data,
          reasonsByRef: data?.reasonsByRef || {},
        };
      } catch {
        return {
          checkId,
          ownerEmail,
          experianResponse: null,
          reasonsByRef: {},
        };
      }
    });

    const results = await Promise.all(tasks);

    return new Map(
      results.map((r) => [
        r.checkId,
        {
          experianResponse: r.experianResponse,
          ownerEmail: r.ownerEmail,
          reasonsByRef: r.reasonsByRef,
        },
      ]),
    );
  }

  const SEARCH_HYDRATE_LIMIT = 2;
  const topMatchedSearchDocs = useMemo(() => {
    if (!isSearchMode) return searchDocs;

    return searchDocs
      .filter(({ id, data }) => {
        if (submittedViaApiOnly && data?.submittedViaApi !== true) return false;
        const hay = makeDocSearchHaystack(id, data);
        return terms.every((t) => hay.includes(t));
      })
      .slice(0, SEARCH_HYDRATE_LIMIT);
  }, [searchDocs, isSearchMode, terms, submittedViaApiOnly]);

  const docsToJoin = isSearchMode ? topMatchedSearchDocs : pageDocs;

  const makeJson = async (res) => {
    if (!res.ok) {
      const t = await res.text();
      throw new Error(`HTTP ${res.status}: ${t.slice(0, 120)}`);
    }
    const ct = res.headers.get("content-type") || "";
    if (!ct.includes("application/json")) {
      const t = await res.text();
      throw new Error(`Expected JSON, got ${ct}. Snippet: ${t.slice(0, 120)}`);
    }
    return res.json();
  };

  useEffect(() => {
    let aborted = false;

    const shouldRefetchDecisions = (decisionKey, basicRows) => {
      if (decisionKey !== lastDecisionKeyRef.current) return true;

      if (
        !Array.isArray(lastMergedRef.current) ||
        lastMergedRef.current.length === 0
      ) {
        return true;
      }

      const now = Date.now();
      const ageMs = now - (lastFetchedAtRef.current || 0);

      const hasProcessing = basicRows.some((r) => {
        const s = normalise(
          r?.decision?.verification?.status || r?.status || "",
        );
        return (
          s.includes("processing") ||
          s.includes("submitted") ||
          s.includes("resub") ||
          s === ""
        );
      });

      const ttl = hasProcessing ? 6000 : 30000;
      return ageMs > ttl;
    };

    (async () => {
      if (!docsToJoin.length) {
        setRows([]);
        return;
      }

      setHydratingRows(true);
      setErr("");

      try {
        const requestDataMap = await fetchRequestDataForDocs(docsToJoin);
        const experianMap = await fetchExperianResponseForDocs(docsToJoin);

        const idvIds = [];
        const docOnlyIds = [];
        const metaBySession = new Map();
        const basicRows = [];

        for (const { data, id: checkId } of docsToJoin) {
          const sessionId = data?.sessionId || "";
          const vendorId = data?.vendorData || checkId;

          const pack = requestDataMap.get(checkId) || {};
          const req = pack.requestData || {};
          const ownerEmail = pack.ownerEmail || data?.createdBy || "";

          const firstName =
            pick(req, "personal.firstName", "personalDetails.firstName") || "";
          const lastName =
            pick(req, "personal.lastName", "personalDetails.lastName") || "";

          const dob =
            pick(
              req,
              "personal.dob",
              "personalDetails.dob",
              "personal.dateOfBirth",
            ) || "";

          const clean = (value) => String(value ?? "").trim();

          const buildingLine = [
            pick(req, "address.buildingNumber"),
            pick(req, "address.buildingName"),
          ]
            .map(clean)
            .filter(Boolean)
            .join(", ");

          const addressParts = [
            buildingLine,
            pick(req, "address.street"),
            pick(req, "address.town"),
            pick(req, "address.county"),
            pick(req, "address.postcode"),
          ];

          const fullAddress = addressParts
            .map(clean)
            .filter(Boolean)
            .join(", ");

          // const addressParts = [
          //   pick(req, "address.buildingNumber"),
          //   pick(req, "address.buildingName"),
          //   pick(req, "address.street"),
          //   pick(req, "address.town"),
          //   pick(req, "address.county"),
          //   pick(req, "address.postcode"),
          // ];

          // const fullAddress = addressParts.filter(Boolean).join(", ");

          // console.log("Address parts for check", checkId, addressParts);
          // console.log("Full address for check", checkId, fullAddress);

          const expPack = experianMap.get(checkId) || {};
          const experianResponse =
            expPack.experianResponse ?? data?.experianResponse ?? null;

          const reasonsByRef =
            expPack.reasonsByRef ?? experianResponse?.reasonsByRef ?? {};

          const row = {
            id: checkId,
            vendorData: vendorId,
            dob,
            fullAddress,
            checkType: data?.checkType || "",
            createdBy: data?.createdBy || "",
            submittedViaApi: data?.submittedViaApi === true,
            sessionUrl: data?.sessionUrl || "",
            forAdviser: data?.forAdviser || "",
            sessionId,
            createdAt: data?.createdAt || null,
            ownerEmail,
            experianResponse,
            reasonsByRef,
            decision: {
              verification: { person: { firstName, lastName } },
            },
          };

          basicRows.push(row);

          if (!sessionId) continue;

          metaBySession.set(sessionId, {
            vendorData: vendorId,
            dob,
            fullAddress,
            forAdviser: data?.forAdviser || "",
            createdBy: data?.createdBy || "",
            submittedViaApi: data?.submittedViaApi === true,
            sessionUrl: data?.sessionUrl || "",
            sessionId,
            createdAt: data?.createdAt || null,
            requestFirstName: firstName,
            requestLastName: lastName,
            experianResponse,
            ownerEmail,
            reasonsByRef,
          });

          const bucket = classifyBucket(data);
          if (bucket === "doconly") docOnlyIds.push(sessionId);
          else if (bucket === "idv") idvIds.push(sessionId);
        }

        const decisionKey = `${idvIds.sort().join("|")}::${docOnlyIds
          .sort()
          .join("|")}`;

        let merged = [];
        const doFetch = shouldRefetchDecisions(decisionKey, basicRows);

        if (doFetch) {
          if (!API_BASE) throw new Error("Missing REACT_APP_AML_SERVER");

          const jobs = [];
          if (idvIds.length) {
            jobs.push(
              fetch(`${API_BASE}get-idv-decisions/?ids=${idsParam(idvIds)}`)
                .then(makeJson)
                .catch((e) => ({ _error: e })),
            );
          }
          if (docOnlyIds.length) {
            jobs.push(
              fetch(
                `${API_BASE}get-docOnly-decisions/?ids=${idsParam(docOnlyIds)}`,
              )
                .then(makeJson)
                .catch((e) => ({ _error: e })),
            );
          }

          const results = jobs.length ? await Promise.all(jobs) : [];
          merged = results.flatMap((r) => (r && !r._error ? r.rows || [] : []));

          if (!merged.length) {
            const firstErr = results.find((r) => r && r._error)?._error;
            if (firstErr) {
              merged = lastMergedRef.current || [];
            }
          }

          lastDecisionKeyRef.current = decisionKey;
          lastMergedRef.current = merged;
          lastFetchedAtRef.current = Date.now();
        } else {
          merged = lastMergedRef.current || [];
        }

        const joinedByKey = new Map(
          merged.map((r) => [r.sessionId || r.id, r]),
        );

        const prevByKey = new Map(rows.map((r) => [r.sessionId || r.id, r]));

        const finalRows = basicRows.map((r) => {
          const key = r.sessionId || r.id;
          const enriched = joinedByKey.get(key) || prevByKey.get(key) || null;

          if (!enriched) return r;

          const sid = enriched?.sessionId || enriched?.id || key;
          const meta = metaBySession.get(sid);
          const existingPerson = enriched?.decision?.verification?.person || {};

          const hydratedFirst =
            meta?.requestFirstName || existingPerson.firstName || "";
          const hydratedLast =
            meta?.requestLastName || existingPerson.lastName || "";

          return {
            ...r,
            ...enriched,
            ownerEmail: meta?.ownerEmail || r.ownerEmail || "",
            experianResponse:
              r.experianResponse ?? enriched.experianResponse ?? null,
            decision: {
              ...enriched.decision,
              verification: {
                ...enriched?.decision?.verification,
                person: {
                  ...existingPerson,
                  firstName: hydratedFirst,
                  lastName: hydratedLast,
                },
              },
            },
          };
        });

        if (!aborted) {
          setRows(finalRows);
          setHasLoadedOnce(true);
        }
      } catch (e) {
        if (!aborted) {
          setErr(e?.message || "Failed to fetch decisions");
          setHasLoadedOnce(true);
        }
      } finally {
        if (!aborted) setHydratingRows(false);
      }
    })();

    return () => {
      aborted = true;
    };
  }, [docsToJoin, userEmail, API_BASE]);

  const filteredForSearch = useMemo(() => {
    if (!isSearchMode) return rows;
    return rows.filter((r) => {
      const hay = makeHaystack(r);
      return terms.every((t) => hay.includes(t));
    });
  }, [rows, terms, isSearchMode]);

  useEffect(() => {
    if (!isSearchMode) return;
    const total = filteredForSearch.length;
    setTotalPages(Math.max(1, Math.ceil(total / pageSize)));
  }, [filteredForSearch.length, isSearchMode, pageSize]);

  const pageRows = useMemo(() => {
    const base = isSearchMode ? filteredForSearch : rows;

    const copy = [...base].sort((a, b) => {
      const tA =
        (a?.createdAt?.toDate
          ? a.createdAt.toDate().getTime()
          : new Date(a?.createdAt || 0).getTime()) || 0;

      const tB =
        (b?.createdAt?.toDate
          ? b.createdAt.toDate().getTime()
          : new Date(b?.createdAt || 0).getTime()) || 0;

      return tB - tA;
    });

    if (isSearchMode) {
      const start = (page - 1) * pageSize;
      return copy.slice(start, start + pageSize);
    }

    return copy;
  }, [rows, filteredForSearch, isSearchMode, page, pageSize]);

  if (!userEmail) {
    return (
      <div className="aml-table-wrap">
        <div className="AML-Text-Body" style={{ padding: 16 }}>
          Sign in to load your AML checks.
        </div>
      </div>
    );
  }

  return (
    <div className="AML-Dashboard-Container">
      {loading && rows.length === 0 && !hasLoadedOnce ? (
        <div className="aml-table-wrap">
          <div className="AML-Text-Body" style={{ padding: 16 }}>
            Loading…
          </div>
        </div>
      ) : rows.length === 0 && err ? (
        <div className="aml-table-wrap">
          <div
            className="AML-Text-Body"
            style={{ padding: 16, color: "var(--Error)" }}
          >
            Please refresh the page or try again later.
          </div>
        </div>
      ) : (
        <>
          <div className="AML-Dashboard-Header">
            <Check2Logo />
            <div className="AML-Dashboard-Actions-Header">
              {viewAll && (
                <EmailFilterDropdown
                  options={emailOptions}
                  value={selectedEmail}
                  onChange={(e) => setSelectedEmail(e.target.value)}
                  placeholder="Filter by email"
                />
              )}
              <div className="AML-Dashboard-Actions-Header">
                {viewAll && (
                  <SearchInput
                    value={q}
                    onChange={setQ}
                    placeholder="Search name, check type, vendor data, session…"
                  />
                )}

                <NewCheckButton
                  label="New Check"
                  onClick={() => navigate("/new-check")}
                />
              </div>
            </div>
          </div>

          {isSearching && (
            <div style={{ padding: "8px 0" }}>
              <Spinner label="Searching check..." />
            </div>
          )}

          <div className="Status-Button-Grid-Table">
            <StatusButton color={"green"} type="Approved" />
            <StatusButton color={"red"} type="Declined" />
            <StatusButton color={"yellow"} type="Referred" />
            <StatusButton color={"purple"} type="Resubmission" />
            <StatusButton color={"blue"} type="Completed" />
            <StatusButton color={"orange"} type="Processing" />
            <StatusButton color={"crimson"} type="Action Required" />
          </div>

          <Announcement />
          {/* {viewAll && (
            <div className="AML-Dashboard-Table-Filter RadioButton-Container">
              <label
                className="Radio-Option"
                onClick={(event) => {
                  event.preventDefault();
                  setSubmittedViaApiOnly((selected) => !selected);
                }}
              >
                <input
                  type="radio"
                  name="aml-dashboard-submission-source"
                  checked={submittedViaApiOnly}
                  readOnly
                />
                <span className="Custom-RadioButton" />
                <div className="Content">Submitted via API</div>
              </label>
            </div>
          )} */}
          <AMLTable rows={pageRows} currentPage={page} pageSize={pageSize} />

          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            siblingCount={1}
            boundaryCount={1}
          />
        </>
      )}
    </div>
  );
}
